import React, { useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { FlexyHeaderForm } from '@europrocurement/flexy-components';
import { Contact } from '@europrocurement/l2d-domain';
import { useForm, Controller } from 'react-hook-form';
import { SelectOptions } from '@europrocurement/l2d-modules/modules/Offres/forms/types';
import SelectField from '@europrocurement/l2d-modules/modules/Offres/components/FormInputs/SelectField';

export type ContactSelectionModalProps = {
    contacts: Contact[];
    currentContactId?: string;
    onContactSelect: (contact: Contact) => void;
    isLoading?: boolean;
};

type FormData = {
    selectedContactId: string;
};

/**
 * Modal de sélection d'un contact parmi une liste de contacts du prescripteur.
 * Permet de modifier l'assignation d'un dossier à un contact spécifique.
 */
const ContactSelectionModal: React.FunctionComponent<ContactSelectionModalProps> = function ({
    contacts,
    currentContactId,
    onContactSelect,
    isLoading = false,
}) {
    const { control, watch, setValue } = useForm<FormData>({
        defaultValues: {
            selectedContactId: currentContactId || '',
        },
    });

    const selectedContactId = watch('selectedContactId');

    /**
     * Met à jour la valeur par défaut si currentContactId change
     */
    useEffect(() => {
        if (currentContactId) {
            setValue('selectedContactId', currentContactId);
        }
    }, [currentContactId, setValue]);

    /**
     * Transforme la liste des contacts en options pour le SelectField.
     */
    const getContactOptions = (): SelectOptions =>
        contacts.map((contact) => ({
            value: contact.id,
            label: `${contact.prenomContact} ${contact.nomContact}`,
            subtitle: contact.emailsContact[0],
        }));

    /**
     * Gère la sélection immédiate d'un contact.
     */
    const handleContactChange = (contactIdentifier: string) => {
        const selectedContact = contacts.find(
            (contact) => contact.id?.toString() === contactIdentifier.toString(),
        );

        if (selectedContact && contactIdentifier !== currentContactId) {
            onContactSelect(selectedContact);
        }
    };

    return (
        <Box sx={{ minWidth: '400px', maxWidth: '500px' }}>
            <FlexyHeaderForm
                label={
                    <Typography
                        variant="h2"
                        color="secondary"
                    >
                        Modifier le contact assigné
                    </Typography>
                }
            />

            <Box sx={{ marginTop: '16px' }}>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ marginBottom: '16px' }}
                >
                    Sélectionnez un contact du prescripteur pour l&apos;assigner à ce dossier :
                </Typography>

                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && contacts.length > 0 && (
                    <Controller
                        name="selectedContactId"
                        control={control}
                        render={({ field }) => (
                            <SelectField
                                {...field}
                                value={selectedContactId}
                                name={field.name}
                                label="Contact à assigner"
                                options={getContactOptions()}
                                placeholder="Sélectionnez un contact"
                                onChange={(value) => {
                                    field.onChange(value);
                                    handleContactChange(value);
                                }}
                                key={`contact-select-${currentContactId}`} // 🎯 Force le re-render
                            />
                        )}
                    />
                )}

                {!isLoading && contacts.length === 0 && (
                    <Box
                        sx={{
                            textAlign: 'center',
                            padding: '32px',
                            color: 'text.secondary',
                        }}
                    >
                        <Typography variant="body2">
                            Aucun contact disponible pour ce prescripteur
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ContactSelectionModal;
